import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchHotspots,
  fetchVideoDebug,
  fetchVideoDebugImageBlobUrl,
  fetchVideoGenerationLog,
} from '../api';
import { useBlobUrl } from '../hooks/useBlobUrl';
import type { GenerationLogResponse, HotspotsDocument, Job, VideoDebugResponse } from '../types';
import { stageLabel } from './videoDebugStages';

type Tab = 'preprocess' | 'damages' | 'hotspots' | 'request' | 'log';

interface VideoDebugModalProps {
  job: Job;
  isOpen: boolean;
  onClose: () => void;
}

/** Cardinal order first; anything unexpected keeps a stable spot after it. */
const POSE_ORDER = ['front', 'left', 'rear', 'right'];

function posesOf(data: VideoDebugResponse): string[] {
  const seen = new Set<string>();
  for (const stage of data.preprocess) {
    for (const img of stage.images) if (img.pose) seen.add(img.pose);
  }
  for (const kf of data.keyframes) seen.add(kf.pose ?? kf.label);
  return [...seen].sort((a, b) => {
    const ia = POSE_ORDER.indexOf(a);
    const ib = POSE_ORDER.indexOf(b);
    return (ia < 0 ? POSE_ORDER.length : ia) - (ib < 0 ? POSE_ORDER.length : ib) || a.localeCompare(b);
  });
}

/** One navigable image. The lightbox loads these itself, so stepping to a
 *  neighbour doesn't depend on its thumbnail having been rendered. */
interface GalleryItem {
  path: string;
  label: string;
  sublabel?: string;
}

/** The group the lightbox can arrow through, and where in it we are. */
interface Opened {
  items: GalleryItem[];
  index: number;
}

// ---------------------------------------------------------------------------
// Image card + lightbox
// ---------------------------------------------------------------------------

interface DebugImageProps {
  jobId: string;
  path: string;
  label: string;
  sublabel?: string;
  onOpen: () => void;
}

function DebugImage({ jobId, path, label, sublabel, onOpen }: DebugImageProps) {
  const fetcher = useCallback(
    (signal: AbortSignal) => fetchVideoDebugImageBlobUrl(jobId, path, signal),
    [jobId, path],
  );
  const url = useBlobUrl(fetcher);

  return (
    <figure className="min-w-0">
      <button
        type="button"
        disabled={!url}
        onClick={onOpen}
        className="block w-full rounded-lg overflow-hidden border border-slate-200 bg-slate-900 hover:border-indigo-400 transition-colors disabled:cursor-default"
        title={sublabel ?? path}
      >
        {url ? (
          <img src={url} alt={label} className="block w-full h-40 object-contain" />
        ) : (
          <div className="h-40 flex items-center justify-center text-xs text-slate-400">
            Loading…
          </div>
        )}
      </button>
      <figcaption className="mt-1.5">
        <div className="text-xs font-medium text-slate-700 truncate">{label}</div>
        {sublabel && (
          <div className="text-[11px] font-mono text-slate-400 truncate">{sublabel}</div>
        )}
      </figcaption>
    </figure>
  );
}

/** Muted stand-in so a missing step reads as an explanation, not a broken image. */
function MissingImage({ label, reason }: { label: string; reason: string }) {
  return (
    <figure className="min-w-0">
      <div className="h-40 rounded-lg border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center px-3 text-center text-[11px] text-slate-400">
        {reason}
      </div>
      <figcaption className="mt-1.5 text-xs font-medium text-slate-400 truncate">{label}</figcaption>
    </figure>
  );
}

function Lightbox({
  jobId,
  opened,
  onClose,
  onNavigate,
}: {
  jobId: string;
  opened: Opened;
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number } | null>(null);

  const { items, index } = opened;
  const item = items[index];
  const filename = item.path.split('/').pop() || item.label;
  const fetcher = useCallback(
    (signal: AbortSignal) => fetchVideoDebugImageBlobUrl(jobId, item.path, signal),
    [jobId, item.path],
  );
  const url = useBlobUrl(fetcher);

  // Wraps at both ends so a long diagnostics strip can be cycled without
  // reaching for the mouse.
  const step = useCallback(
    (delta: number) => {
      if (items.length < 2) return;
      onNavigate((index + delta + items.length) % items.length);
    },
    [index, items.length, onNavigate],
  );

  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [item.path]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, step]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/90 flex flex-col"
      // Stops at this boundary: the debug modal and the player modal behind it
      // both close on a backdrop click.
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      onWheel={(e) => {
        e.preventDefault();
        setScale((s) => Math.min(8, Math.max(1, s * (e.deltaY < 0 ? 1.15 : 1 / 1.15))));
      }}
    >
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 text-white/90"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{item.label}</div>
          <div className="text-[11px] font-mono text-white/50 truncate">{filename}</div>
        </div>
        <div className="flex items-center gap-3 shrink-0 text-xs">
          {items.length > 1 && (
            <span className="text-white/50 tabular-nums">
              {index + 1} / {items.length}
            </span>
          )}
          <span className="text-white/50">{Math.round(scale * 100)}%</span>
          {url && (
            <a
              href={url}
              download={filename}
              className="px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 transition-colors"
            >
              Download
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden flex items-center justify-center relative">
        {items.length > 1 && (
          <>
            <NavArrow side="left" onClick={() => step(-1)} />
            <NavArrow side="right" onClick={() => step(1)} />
          </>
        )}
        {!url && <div className="text-sm text-white/50">Loading…</div>}
        {url && (
        <img
          src={url}
          alt={item.label}
          draggable={false}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={() => {
            setScale((s) => (s > 1 ? 1 : 2));
            setOffset({ x: 0, y: 0 });
          }}
          onPointerDown={(e) => {
            dragRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e) => {
            if (!dragRef.current) return;
            setOffset({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
          }}
          onPointerUp={() => {
            dragRef.current = null;
          }}
          className="max-h-full max-w-full object-contain select-none"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            cursor: scale > 1 ? 'grab' : 'default',
          }}
        />
        )}
      </div>
    </div>
  );
}

function NavArrow({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={side === 'left' ? 'Previous image' : 'Next image'}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`absolute z-10 top-1/2 -translate-y-1/2 ${
        side === 'left' ? 'left-3' : 'right-3'
      } w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white text-2xl leading-none flex items-center justify-center transition-colors`}
    >
      {side === 'left' ? '‹' : '›'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

function PreprocessTab({
  jobId,
  data,
  onOpen,
}: {
  jobId: string;
  data: VideoDebugResponse;
  onOpen: (o: Opened) => void;
}) {
  const poses = useMemo(() => posesOf(data), [data]);
  const [pose, setPose] = useState<string>(poses[0] ?? '');

  useEffect(() => {
    if (poses.length && !poses.includes(pose)) setPose(poses[0]);
  }, [poses, pose]);

  // The chain a single pose walked, in the order the pipeline wrote it: the
  // deterministic composite and the model's shadow pass come before the final
  // stabilized frame, and the persisted keyframe is what generation received.
  const chain = useMemo(() => {
    if (!pose) return [];
    const variantRank = (label: string) =>
      label.endsWith('_composite') ? 0 : label.endsWith('_model') ? 1 : 2;
    const items: { label: string; sublabel: string; path: string }[] = [];
    for (const stage of data.preprocess) {
      const imgs = stage.images.filter((img) => (img.pose ?? '') === pose);
      for (const img of imgs.sort((a, b) => variantRank(a.label) - variantRank(b.label))) {
        const variant = img.label.startsWith(`${pose}_`) ? img.label.slice(pose.length + 1) : '';
        items.push({
          label: variant ? `${stageLabel(stage.stage)} · ${variant}` : stageLabel(stage.stage),
          sublabel: `${stage.stage}/${img.label}`,
          path: img.path,
        });
      }
    }
    const kf = data.keyframes.find((k) => (k.pose ?? k.label) === pose);
    if (kf) items.push({ label: 'Model input', sublabel: `keyframes/${kf.label}`, path: kf.path });
    return items;
  }, [data, pose]);

  if (!poses.length) {
    return (
      <EmptyState
        title="No preprocessing images"
        body="This run generated straight from the source frames (compose_mode 'off') and kept no keyframes, so there is nothing to show."
      />
    );
  }

  return (
    <div>
      {!data.preprocess.length && (
        <p className="mb-4 text-xs text-slate-500">
          No per-stage images for this run (compose_mode was 'off') — showing the keyframes handed
          to the video model.
        </p>
      )}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {poses.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPose(p)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              p === pose
                ? 'bg-indigo-50 text-indigo-700 border-indigo-300'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {p}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {chain.map((item, i) => (
          <DebugImage
            key={item.path}
            jobId={jobId}
            path={item.path}
            label={item.label}
            sublabel={item.sublabel}
            onOpen={() => onOpen({ items: chain, index: i })}
          />
        ))}
      </div>
    </div>
  );
}

function DamagesTab({
  jobId,
  data,
  onOpen,
}: {
  jobId: string;
  data: VideoDebugResponse;
  onOpen: (o: Opened) => void;
}) {
  type Selection = { camera: string; slug: string | null };
  const first = useMemo<Selection | null>(() => {
    for (const cam of data.cameras) {
      if (cam.damages.length) return { camera: cam.camera, slug: cam.damages[0].slug };
      if (cam.match) return { camera: cam.camera, slug: null };
    }
    return null;
  }, [data.cameras]);
  const [selected, setSelected] = useState<Selection | null>(first);

  useEffect(() => setSelected(first), [first]);

  if (!data.damage_debug_present) {
    return (
      <EmptyState
        title="No damage debug images"
        body={
          data.has_hotspots
            ? 'This run localized damages without persisting the annotated images. Turn on "Damage debug images" (per-run checkbox on the submit form, or VIDEO_DAMAGE_DEBUG on the server), then either re-run the scan or hit "+ Damages" in the 360 viewer to generate them for this job.'
            : 'No hotspots were computed for this job, so there is nothing to localize. Compute them from the 360 viewer first.'
        }
      />
    );
  }

  const camera = data.cameras.find((c) => c.camera === selected?.camera);
  const damage = camera?.damages.find((d) => d.slug === selected?.slug);

  // The localization trail in pipeline order, then the front/rear diagnostics.
  // Absent steps are simply left out, so the arrows only ever land on a real
  // image. ``plane`` has no placeholder: front/rear cameras match the keyframe
  // directly and never produce one, which is expected rather than missing.
  const trail: { label: string; sublabel: string; path?: string | null; missing?: string }[] = [
    {
      label: '1 · Original frame',
      sublabel: 'damage boxed on the arch frame',
      path: damage?.frame,
      missing: 'not written',
    },
    {
      label: '2 · Camera stitch',
      sublabel: 'box mapped onto the stitched plane',
      path: damage?.plane,
    },
    {
      label: '3 · Keyframe',
      sublabel: 'anchor point mapped into the video keyframe',
      path: damage?.keyframe,
      missing: 'anchor could not be mapped',
    },
  ];
  const gallery: GalleryItem[] = [
    ...trail.flatMap((s) => (s.path ? [{ path: s.path, label: s.label, sublabel: s.sublabel }] : [])),
    ...(damage?.extras ?? []).map((e) => ({
      path: e.path,
      label: e.label,
      sublabel: e.path.split('/').pop(),
    })),
  ];
  const indexOf = (path: string) => gallery.findIndex((g) => g.path === path);

  return (
    <div className="flex gap-5 min-h-0">
      <aside className="w-56 shrink-0 overflow-y-auto max-h-[62vh] pr-1">
        {data.cameras.map((cam) => (
          <div key={cam.camera} className="mb-3">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-2 mb-1">
              {cam.camera}
            </div>
            {cam.match && (
              <SidebarItem
                active={selected?.camera === cam.camera && selected?.slug === null}
                onClick={() => setSelected({ camera: cam.camera, slug: null })}
                label="Stitch registration"
                mono={false}
              />
            )}
            {cam.damages.map((d) => (
              <SidebarItem
                key={d.slug}
                active={selected?.camera === cam.camera && selected?.slug === d.slug}
                onClick={() => setSelected({ camera: cam.camera, slug: d.slug })}
                label={d.slug}
                mono
              />
            ))}
          </div>
        ))}
      </aside>

      <div className="flex-1 min-w-0 overflow-y-auto max-h-[62vh]">
        {camera && selected?.slug === null && camera.match && (
          <DebugImage
            jobId={jobId}
            path={camera.match}
            label={`${camera.camera} — stitch ↔ keyframe registration`}
            sublabel={camera.match.split('/').pop()}
            onOpen={() =>
              onOpen({
                items: [
                  {
                    path: camera.match!,
                    label: `${camera.camera} — stitch ↔ keyframe registration`,
                  },
                ],
                index: 0,
              })
            }
          />
        )}
        {damage && (
          <div>
            <div className="text-xs font-mono text-slate-500 mb-3 break-all">{damage.slug}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {trail.map((slot) =>
                slot.path ? (
                  <DebugImage
                    key={slot.label}
                    jobId={jobId}
                    path={slot.path}
                    label={slot.label}
                    sublabel={slot.sublabel}
                    onOpen={() => onOpen({ items: gallery, index: indexOf(slot.path!) })}
                  />
                ) : slot.missing ? (
                  <MissingImage key={slot.label} label={slot.label} reason={slot.missing} />
                ) : null,
              )}
            </div>
            {damage.extras.length > 0 && (
              <div className="mt-6">
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Front / rear diagnostics
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {damage.extras.map((extra) => (
                    <DebugImage
                      key={extra.path}
                      jobId={jobId}
                      path={extra.path}
                      label={extra.label}
                      sublabel={extra.path.split('/').pop()}
                      onOpen={() => onOpen({ items: gallery, index: indexOf(extra.path) })}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SidebarItem({
  active,
  onClick,
  label,
  mono,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  mono: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`w-full text-left px-2 py-1.5 rounded-md text-xs truncate transition-colors ${
        mono ? 'font-mono' : 'font-medium'
      } ${active ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
    >
      {label}
    </button>
  );
}

function HotspotsTab({
  doc,
  loading,
  error,
}: {
  doc: HotspotsDocument | null;
  loading: boolean;
  error: string | null;
}) {
  if (loading) return <div className="text-sm text-slate-400">Loading hotspots.json…</div>;
  if (error) return <EmptyState title="Could not load hotspots.json" body={error} />;
  if (!doc) {
    return (
      <EmptyState
        title="No hotspots.json"
        body="Hotspots have not been computed for this job yet. Compute them from the 360 viewer."
      />
    );
  }
  const counts = doc.hotspots.reduce<Record<string, number>>((acc, h) => {
    const kind = h.kind ?? 'unknown';
    acc[kind] = (acc[kind] ?? 0) + 1;
    return acc;
  }, {});
  return (
    <div>
      <div className="flex items-center gap-3 mb-3 text-xs text-slate-500">
        <span>
          {doc.hotspots.length} hotspot{doc.hotspots.length === 1 ? '' : 's'}
        </span>
        {Object.entries(counts).map(([kind, n]) => (
          <span key={kind} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
            {kind}: {n}
          </span>
        ))}
      </div>
      <pre className="text-[11px] leading-relaxed font-mono bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-[58vh] overflow-auto whitespace-pre-wrap">
        {JSON.stringify(doc, null, 2)}
      </pre>
    </div>
  );
}

function KeyValueTable({ rows }: { rows: [string, React.ReactNode][] }) {
  return (
    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
      {rows.map(([key, value]) => (
        <div key={key} className="flex gap-4 px-3 py-1.5 text-xs">
          <div className="w-44 shrink-0 text-slate-500">{key}</div>
          <div className="min-w-0 flex-1 text-slate-800 break-all">{value}</div>
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** Renders a value the way it reads best in a table cell, `null` included. */
function renderValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-slate-400">—</span>;
  }
  if (typeof value === 'boolean') {
    return (
      <span className={value ? 'text-emerald-700' : 'text-slate-500'}>{value ? 'yes' : 'no'}</span>
    );
  }
  if (typeof value === 'object') return <span className="font-mono">{JSON.stringify(value)}</span>;
  return <span className="font-mono">{String(value)}</span>;
}

function RequestTab({ job }: { job: Job }) {
  const [copied, setCopied] = useState(false);
  const params = job.task_params;
  // What the run actually used; the task_params snapshot is the fallback for
  // jobs that never reached the processing stage.
  const genCfg = job.config_used ?? params?.gen_cfg ?? null;

  const requestRows = useMemo<[string, React.ReactNode][]>(() => {
    if (!params) return [];
    const known = ['source', 'image_source', 'create_hotspots', 'damage_debug', 'callback_url'];
    const rows: [string, React.ReactNode][] = known.map((key) => [key, renderValue(params[key])]);
    for (const [key, value] of Object.entries(params)) {
      if (key === 'gen_cfg' || known.includes(key)) continue;
      rows.push([key, renderValue(value)]);
    }
    return rows;
  }, [params]);

  const usage = Object.values(job.fal_usage ?? {});
  const totalCost = usage.reduce((sum, u) => sum + (u.direct_cost || 0), 0);

  const copyConfig = async () => {
    if (!genCfg) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(genCfg, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked — the JSON is on screen to select by hand.
    }
  };

  return (
    <div>
      <Section title="Job">
        <KeyValueTable
          rows={[
            ['scan_id', renderValue(job.scan_id)],
            ['job_id', renderValue(job.job_id)],
            ['status', renderValue(job.status)],
            ['source', renderValue(job.source)],
            ['service_version', renderValue(job.service_version)],
            ['created_at', renderValue(job.created_at)],
            ['started_at', renderValue(job.started_at)],
            ['completed_at', renderValue(job.completed_at)],
            [
              'duration',
              job.duration_seconds === null
                ? renderValue(null)
                : renderValue(`${job.duration_seconds.toFixed(1)}s`),
            ],
            ['organization_id', renderValue(job.organization_id)],
            ['site_id', renderValue(job.site_id)],
            [
              'uvcamp_link',
              job.uvcamp_link ? (
                <a
                  href={job.uvcamp_link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 hover:underline font-mono"
                >
                  {job.uvcamp_link}
                </a>
              ) : (
                renderValue(null)
              ),
            ],
            ['video_path', renderValue(job.video_path)],
            ['error', renderValue(job.error)],
          ]}
        />
      </Section>

      <Section title="Request params">
        {requestRows.length ? (
          <KeyValueTable rows={requestRows} />
        ) : (
          <p className="text-xs text-slate-500">
            No request params recorded — this job predates the dispatch snapshot.
          </p>
        )}
      </Section>

      <Section title="Generation config (as used)">
        {genCfg ? (
          <div>
            <button
              type="button"
              onClick={copyConfig}
              className="mb-2 px-2.5 py-1 rounded-md text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            >
              {copied ? 'Copied!' : 'Copy config'}
            </button>
            <pre className="text-[11px] leading-relaxed font-mono bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-[40vh] overflow-auto whitespace-pre-wrap">
              {JSON.stringify(genCfg, null, 2)}
            </pre>
          </div>
        ) : (
          <p className="text-xs text-slate-500">No config recorded for this job.</p>
        )}
      </Section>

      {usage.length > 0 && (
        <Section title={`FAL usage — $${totalCost.toFixed(4)} total`}>
          <KeyValueTable
            rows={usage.map((u) => [
              u.endpoint,
              <span className="font-mono">
                {u.calls} call{u.calls === 1 ? '' : 's'} · ${u.direct_cost.toFixed(4)}
              </span>,
            ])}
          />
        </Section>
      )}
    </div>
  );
}

/** Quote only when the value would otherwise break the row. */
function csvCell(value: string): string {
  return /["\n\r,]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function toCsv({ columns, rows }: GenerationLogResponse): string {
  return [
    columns.map(csvCell).join(','),
    ...rows.map((row) => columns.map((col) => csvCell(row[col] ?? '')).join(',')),
  ].join('\n');
}

function GenerationLogTab({
  data,
  loading,
  error,
}: {
  data: GenerationLogResponse | null;
  loading: boolean;
  error: string | null;
}) {
  const [copied, setCopied] = useState(false);

  const copyCsv = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(toCsv(data));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked — the table is on screen to select by hand.
    }
  };

  if (loading) return <div className="text-sm text-slate-400">Loading generation_log.csv…</div>;
  if (error) return <EmptyState title="Could not load generation_log.csv" body={error} />;
  if (!data || !data.columns.length || !data.rows.length) {
    return (
      <EmptyState
        title="No generation log"
        body="This job has no generation_log.csv — it either predates the per-attempt log or never reached a generation call."
      />
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={copyCsv}
        className="mb-3 px-2.5 py-1 rounded-md text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
      >
        {copied ? 'Copied!' : 'Copy log CSV'}
      </button>
      {/* This wrapper owns BOTH axes and is height-capped. If it only owned
          overflow-x, its horizontal scrollbar would sit at the bottom of a
          table thousands of pixels tall, far below the modal's visible area,
          and the operator could never reach it. */}
      <div className="overflow-auto max-h-[calc(92vh-12rem)] rounded-lg border border-slate-200">
        <table className="data-table data-table-scroll min-w-full text-xs">
          <thead>
            <tr>
              {data.columns.map((col) => (
                <th
                  key={col}
                  className="sticky top-0 z-10 px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap border-b border-slate-200"
                >
                  {col.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                {data.columns.map((col) => {
                  const value = row[col] ?? '';
                  return (
                    <td
                      key={col}
                      data-label={col.replace(/_/g, ' ')}
                      className="px-3 py-1.5 text-slate-700 align-top font-mono"
                    >
                      {/* A single multi-hundred-character `error` value would
                          otherwise stretch the table past 9000px. */}
                      <div className="max-w-[28rem] truncate" title={value.length > 40 ? value : undefined}>
                        {value}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="max-w-xl">
      <div className="text-sm font-medium text-slate-700">{title}</div>
      <p className="mt-1 text-xs text-slate-500 leading-relaxed">{body}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export function VideoDebugModal({ job, isOpen, onClose }: VideoDebugModalProps) {
  const { job_id: jobId, scan_id: scanId } = job;
  const [tab, setTab] = useState<Tab>('preprocess');
  const [data, setData] = useState<VideoDebugResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [opened, setOpened] = useState<Opened | null>(null);

  const [hotspots, setHotspots] = useState<HotspotsDocument | null>(null);
  const [hotspotsError, setHotspotsError] = useState<string | null>(null);
  const [hotspotsLoading, setHotspotsLoading] = useState(false);
  const hotspotsFetched = useRef(false);
  const [copyNote, setCopyNote] = useState<string | null>(null);

  const [genLog, setGenLog] = useState<GenerationLogResponse | null>(null);
  const [genLogError, setGenLogError] = useState<string | null>(null);
  const [genLogLoading, setGenLogLoading] = useState(false);
  const genLogFetched = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setData(null);
      setError(null);
      setHotspots(null);
      setHotspotsError(null);
      setGenLog(null);
      setGenLogError(null);
      setOpened(null);
      setTab('preprocess');
      hotspotsFetched.current = false;
      genLogFetched.current = false;
      return;
    }
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    fetchVideoDebug(jobId, ac.signal)
      .then((res) => !ac.signal.aborted && setData(res))
      .catch((err: Error) => !ac.signal.aborted && setError(err.message))
      .finally(() => !ac.signal.aborted && setLoading(false));
    return () => ac.abort();
  }, [isOpen, jobId]);

  // hotspots.json backs both the JSON tab and the copy button, so load it once
  // on whichever the user reaches first.
  const loadHotspots = useCallback(async (): Promise<HotspotsDocument | null> => {
    if (hotspotsFetched.current) return hotspots;
    hotspotsFetched.current = true;
    setHotspotsLoading(true);
    try {
      const doc = await fetchHotspots(jobId);
      setHotspots(doc);
      return doc;
    } catch (err) {
      setHotspotsError((err as Error).message);
      return null;
    } finally {
      setHotspotsLoading(false);
    }
  }, [jobId, hotspots]);

  useEffect(() => {
    if (isOpen && tab === 'hotspots') void loadHotspots();
  }, [isOpen, tab, loadHotspots]);

  // The CSV is finished by the time the job is, so one fetch on the first
  // visit to the tab is enough.
  const loadGenerationLog = useCallback(async () => {
    if (genLogFetched.current) return;
    genLogFetched.current = true;
    setGenLogLoading(true);
    try {
      setGenLog(await fetchVideoGenerationLog(jobId));
    } catch (err) {
      setGenLogError((err as Error).message);
    } finally {
      setGenLogLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (isOpen && tab === 'log') void loadGenerationLog();
  }, [isOpen, tab, loadGenerationLog]);

  // A job switch while the modal stays open can retire the tab under us.
  useEffect(() => {
    if (tab === 'log' && data && !data.has_generation_log) setTab('preprocess');
  }, [tab, data]);

  const handleCopyHotspots = useCallback(async () => {
    const doc = (await loadHotspots()) ?? hotspots;
    const note = await (async () => {
      if (!doc) return 'Nothing to copy';
      try {
        await navigator.clipboard.writeText(JSON.stringify(doc, null, 2));
        return 'Copied!';
      } catch {
        return 'Copy blocked';
      }
    })();
    setCopyNote(note);
    setTimeout(() => setCopyNote(null), 1800);
  }, [loadHotspots, hotspots]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      // The lightbox owns Escape while it's up.
      if (e.key === 'Escape' && !opened) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, opened]);

  if (!isOpen) return null;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'preprocess', label: 'Preprocess' },
    { id: 'damages', label: 'Damages' },
    { id: 'hotspots', label: 'Hotspots JSON' },
    { id: 'request', label: 'Request' },
    ...(data?.has_generation_log ? [{ id: 'log' as Tab, label: 'Generation Log' }] : []),
  ];

  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4"
      // Rendered inside the player modal, whose backdrop also closes on click.
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className="w-full max-w-7xl max-h-[92vh] bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-slate-400">Debug panel</div>
            <div className="text-sm font-mono text-slate-900 truncate">{scanId}</div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    tab === t.id
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={handleCopyHotspots}
              disabled={hotspotsLoading}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-60"
            >
              {copyNote ?? 'Copy hotspots data'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0">
          {/* The Request tab reads the job row we already have, so it doesn't
              wait on (or care about) the artifact manifest. */}
          {loading && tab !== 'request' && (
            <div className="text-sm text-slate-400">Loading debug artifacts…</div>
          )}
          {error && tab !== 'request' && (
            <EmptyState title="Could not load debug artifacts" body={error} />
          )}
          {tab === 'request' && <RequestTab job={job} />}
          {data && tab === 'preprocess' && (
            <PreprocessTab jobId={jobId} data={data} onOpen={setOpened} />
          )}
          {data && tab === 'damages' && (
            <DamagesTab jobId={jobId} data={data} onOpen={setOpened} />
          )}
          {tab === 'hotspots' && (
            <HotspotsTab doc={hotspots} loading={hotspotsLoading} error={hotspotsError} />
          )}
          {data && tab === 'log' && (
            <GenerationLogTab data={genLog} loading={genLogLoading} error={genLogError} />
          )}
        </div>

        {data && (
          <div className="px-6 py-2.5 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
            <span>storage: {data.storage}</span>
            <span>
              {data.preprocess.length} preprocess stage{data.preprocess.length === 1 ? '' : 's'}
            </span>
            <span>{data.keyframes.length} keyframes</span>
            <span>
              {data.cameras.length} camera{data.cameras.length === 1 ? '' : 's'} with damage debug
            </span>
          </div>
        )}
      </div>

      {opened && (
        <Lightbox
          jobId={job.job_id}
          opened={opened}
          onClose={() => setOpened(null)}
          onNavigate={(index) => setOpened((o) => (o ? { ...o, index } : o))}
        />
      )}
    </div>
  );
}
